/**
 * SocialCardsModal — modal Admin para pré-visualizar e descarregar cards
 * de partilha para redes sociais. Gerado dinamicamente pelo backend:
 *   GET /api/admin/routes/{route_id}/social-card?format=facebook|instagram|story
 *
 * Uso:
 *   <SocialCardsModal open={open} onClose={setOpen} routeId={id} routeName={name} />
 *
 * Formatos:
 *   - Facebook   1200×630   (Feed FB / LinkedIn / X)
 *   - Instagram  1080×1080  (IG Feed)
 *   - Story      1080×1920  (IG / FB Story)
 */
import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { reportError } from '@/lib/errorReporter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = [
  { id: 'facebook',  label: 'Facebook',    size: '1200×630',   ratio: 1200 / 630,
    hint: 'Feed FB · LinkedIn · X (Twitter)' },
  { id: 'instagram', label: 'Instagram',   size: '1080×1080',  ratio: 1,
    hint: 'IG Feed Square' },
  { id: 'story',     label: 'Story',       size: '1080×1920',  ratio: 1080 / 1920,
    hint: 'IG / FB Stories · TikTok' },
];

export default function SocialCardsModal({ open, onClose, routeId, routeName }) {
  const [active, setActive] = useState('facebook');
  const [downloading, setDownloading] = useState(false);
  const [imgError, setImgError] = useState(null);
  // Progress bar (Ago/2026): mostra progresso real (streamed) durante a
  // geração/download de cada card. `progress` 0–100. `progressLabel` ex:
  // "Facebook · 42%" ou "Facebook · A gerar…" (indeterminado se sem
  // Content-Length). `progressAll` conta os cards já feitos no botão "3".
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [progressAll, setProgressAll] = useState(null); // {done, total}

  // Cache-busting só ao abrir para forçar re-fetch se a cover mudou
  const cacheBuster = useMemo(() => (open ? Date.now() : 0), [open, routeId]);

  const cardUrl = (fmt) =>
    `${API}/admin/routes/${routeId}/social-card?format=${fmt}&_=${cacheBuster}`;

  const activeCfg = FORMATS.find((f) => f.id === active);

  const handleImgError = async () => {
    let detail = 'HTTP failure';
    let status = 0;
    try {
      const res = await fetch(cardUrl(active), { credentials: 'include' });
      status = res.status;
      const body = await res.text();
      try { detail = JSON.parse(body).detail || body.slice(0, 300); }
      catch { detail = body.slice(0, 300); }
    } catch (e) {
      detail = e?.message || 'network error';
    }
    setImgError({ status, detail, fmt: active });
    toast.error(`Card ${active} falhou (${status}): ${detail}`);
    reportError({
      level: 'error',
      category: 'social_card_render',
      message: `Social card ${active} falhou (${routeId}): ${detail}`,
      url: `/admin/routes/${routeId}/social-card?format=${active}`,
      http_status: status || undefined,
    });
  };

  const download = async (fmt) => {
    setDownloading(true);
    const label = FORMATS.find(f => f.id === fmt)?.label || fmt;
    setProgress(0);
    setProgressLabel(`${label} · a gerar…`);
    try {
      const res = await fetch(cardUrl(fmt), { credentials: 'include' });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 300)}`);
      }
      // Progresso streamed a partir do ReadableStream. Se o servidor não
      // enviar Content-Length (chunked encoding), mostra barra indeterminada
      // (progress fica em 0 mas com label).
      const total = Number(res.headers.get('content-length') || 0);
      const reader = res.body?.getReader?.();
      const chunks = [];
      let received = 0;
      if (reader) {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          if (total > 0) {
            const pct = Math.min(99, Math.round((received / total) * 100));
            setProgress(pct);
            setProgressLabel(`${label} · ${pct}%`);
          } else {
            // Indeterminado: mostra KB descarregados
            setProgressLabel(`${label} · ${Math.round(received / 1024)} KB`);
          }
        }
      }
      const blob = reader ? new Blob(chunks, { type: 'image/png' }) : await res.blob();
      setProgress(100);
      setProgressLabel(`${label} · 100%`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (routeName || 'route').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      a.download = `tectur_${safeName}_${fmt}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`📥 ${label} descarregado`);
    } catch (err) {
      const msg = err?.message || 'Falha desconhecida';
      toast.error(`Falha a descarregar: ${msg}`);
      reportError({
        level: 'error',
        category: 'social_card_download',
        message: `Social card download ${fmt} falhou (${routeId}): ${msg}`,
        url: `/admin/routes/${routeId}/social-card?format=${fmt}`,
      });
    } finally {
      // Limpa a barra 1.2s depois para o utilizador ver o 100%
      setTimeout(() => {
        setProgress(0);
        setProgressLabel('');
      }, 1200);
      setDownloading(false);
    }
  };

  const downloadAll = async () => {
    setProgressAll({ done: 0, total: FORMATS.length });
    for (let i = 0; i < FORMATS.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await download(FORMATS[i].id);
      setProgressAll({ done: i + 1, total: FORMATS.length });
    }
    setTimeout(() => setProgressAll(null), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="social-cards-dialog"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '2px', textTransform: 'uppercase',
              color: '#F2EBDD',
            }}
          >
            📱 Cards sociais · {routeName || '—'}
          </DialogTitle>
          <DialogDescription>
            Descarrega os cards em 3 formatos e publica em Facebook, Instagram
            e Stories. Fundo é a foto de capa da rota + QR code para a app.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs por formato */}
        <div className="flex gap-2 flex-wrap mb-3 mt-2">
          {FORMATS.map((f) => (
            <button
              key={f.id}
              onClick={() => setActive(f.id)}
              data-testid={`social-card-tab-${f.id}`}
              style={{
                padding: '8px 14px',
                borderRadius: 4,
                border: `1px solid ${active === f.id ? '#E87A2A' : '#3D362E'}`,
                background: active === f.id ? '#E87A2A' : 'transparent',
                color: active === f.id ? '#0F0D0B' : '#C4A275',
                fontSize: 11, fontWeight: 800, letterSpacing: 1,
                fontFamily: "'JetBrains Mono', monospace",
                textTransform: 'uppercase', cursor: 'pointer',
              }}
            >
              {f.label} · {f.size}
            </button>
          ))}
        </div>

        {/* Preview */}
        <div
          style={{
            background: '#0F0D0B',
            border: '1px solid #3D362E',
            borderRadius: 6,
            padding: 12,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 300,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: active === 'story' ? 320
                     : active === 'instagram' ? 480 : 720,
              aspectRatio: activeCfg?.ratio || 1,
              background: '#1A1613',
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Loading skeleton + img (usa <img> para browser tratar do cache) */}
            <img
              key={`${routeId}-${active}-${cacheBuster}`}
              src={cardUrl(active)}
              alt={`Card ${activeCfg?.label}`}
              data-testid={`social-card-preview-${active}`}
              style={{ width: '100%', height: '100%',
                       display: (imgError && imgError.fmt === active) ? 'none' : 'block',
                       objectFit: 'contain' }}
              onLoad={() => setImgError(null)}
              onError={handleImgError}
            />
            {imgError && imgError.fmt === active && (
              <div
                data-testid="social-card-error-box"
                style={{
                  position: 'absolute', inset: 0,
                  padding: 20, textAlign: 'center',
                  display: 'flex', flexDirection: 'column',
                  justifyContent: 'center', alignItems: 'center',
                  color: '#EF4444', fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12, lineHeight: 1.6,
                }}
              >
                ⚠️ FALHA A GERAR O CARD<br />
                <span style={{ color: '#C4A275', fontSize: 11, marginTop: 6 }}>
                  HTTP {imgError.status || '?'} · {imgError.detail}
                </span>
                <span style={{ color: '#8a7f6f', fontSize: 10, marginTop: 8 }}>
                  Erro enviado para o painel ERROS.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Barra de progresso (Ago/2026) — visível apenas durante download */}
        {(downloading || progressAll) && (
          <div
            className="mt-3"
            data-testid="social-card-progress"
            style={{
              background: 'rgba(15,13,11,0.6)',
              border: '1px solid #3D362E',
              borderRadius: 6,
              padding: '10px 12px',
            }}
          >
            <div
              className="flex items-center justify-between mb-2"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11, color: '#C4A275', letterSpacing: 1,
              }}
            >
              <span>
                {progressAll
                  ? `PACK · ${progressAll.done}/${progressAll.total} · ${progressLabel}`
                  : (progressLabel || 'A processar…')}
              </span>
              <span style={{ color: '#E87A2A', fontWeight: 800 }}>
                {progress > 0 ? `${progress}%` : ''}
              </span>
            </div>
            <div
              style={{
                height: 6, borderRadius: 3,
                background: 'rgba(196,162,117,0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {progress > 0 ? (
                <div
                  data-testid="social-card-progress-bar"
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg,#E87A2A,#F5A96A)',
                    transition: 'width 120ms linear',
                  }}
                />
              ) : (
                // Barra indeterminada (sem Content-Length) — animação sweep
                <div
                  data-testid="social-card-progress-bar-indeterminate"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(90deg, transparent 0%, #E87A2A 50%, transparent 100%)',
                    animation: 'socialCardSweep 1.2s linear infinite',
                    width: '40%',
                  }}
                />
              )}
              <style>{`
                @keyframes socialCardSweep {
                  0% { transform: translateX(-100%); }
                  100% { transform: translateX(350%); }
                }
              `}</style>
            </div>
          </div>
        )}

        {/* Descrição + Actions */}
        <div className="flex items-center gap-2 flex-wrap mt-3">
          <div className="flex-1 text-xs text-muted-foreground"
               style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {activeCfg?.hint}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => download(active)}
            disabled={downloading}
            data-testid={`social-card-download-${active}`}
            style={{
              fontSize: 11, fontWeight: 800, letterSpacing: 1,
              fontFamily: "'JetBrains Mono', monospace",
              textTransform: 'uppercase',
            }}
          >
            {downloading ? '⏳' : '📥'} DESCARREGAR
          </Button>
          <Button
            size="sm"
            onClick={downloadAll}
            disabled={downloading}
            data-testid="social-card-download-all"
            style={{
              background: '#E87A2A', color: '#0F0D0B',
              fontSize: 11, fontWeight: 800, letterSpacing: 1,
              fontFamily: "'JetBrains Mono', monospace",
              textTransform: 'uppercase',
            }}
          >
            📦 DESCARREGAR 3
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
